import { COMPLETE } from '@/constants/action';
import { actionsKeys, celebrateReflectionWeekScopeRoot } from '@/gql/actions/keys';
import { QuickCreateActionPayload } from '@/gql/actions/types';
import { keys } from '@/gql/global/keys';
import { queryClient } from '@/services/graphql/queryClient';
import {
  Action,
  ActionUpdateWhere,
} from '@/types/actions';
import { Category } from '@/types/category';
import {
  fastForwardRecurrenceCursor,
  formatDateToString,
  getExceptionDates,
  getRecurringInstancesInRangeCompletionSummary,
  isExceptionDate,
  isValidWeeklyDay,
  shouldIncrementByWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import { validateRecurrence } from '@/utils/events';
import {
  add,
  isBefore,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { filter, orderBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { getSecondsFromDuration, humanDuration } from '.';
import { getMonthlyCalendarQueryKey } from './query';
import { invalidateQueries, refetchQueries } from './tanStackQuery';

export type OrderFieldType = 'categoryOrder' | 'projectOrder';

const scheduleHasValue = (value: unknown) => value != null && String(value).trim() !== '';

/**
 * Schedule fields for mutations:
 * - scheduledDate may be set while scheduledTime is null/absent (day-only).
 * - If scheduledTime has a value, scheduledDate must have a value too; otherwise time is cleared.
 * - Both may be null.
 * When scheduledDate is cleared (or absent) on a partial update, scheduledTime is forced to null
 * so we never leave a time without a date.
 */
export function pairedActionScheduleFields<T extends Record<string, unknown>>(input: T): T {
  const hasDate = Object.prototype.hasOwnProperty.call(input, 'scheduledDate');
  const hasTime = Object.prototype.hasOwnProperty.call(input, 'scheduledTime');
  if (!hasDate && !hasTime) {
    return input;
  }
  const actionFields = { ...input } as T & { scheduledDate?: unknown; scheduledTime?: unknown };

  const dateSet = hasDate && scheduleHasValue(actionFields.scheduledDate);
  const timeSet = hasTime && scheduleHasValue(actionFields.scheduledTime);

  const shouldClearTime = hasDate && !dateSet && (timeSet || !hasTime);
  if (shouldClearTime) {
    actionFields.scheduledTime = null;
  }

  return actionFields as T;
}

export function calculateTotalMinutesBasedOnDuration(duration?: string | null) {
  const [hour, minute] = (duration || '00:00:00').split(':');
  return Number(hour) * 60 + Number(minute);
}

export function calculateTotalDuration(actions: Action[]) {
  return actions.reduce((acc, action) => {
    return acc + calculateTotalMinutesBasedOnDuration(action.duration || '00:00:00');
  }, 0);
}

function skipRecurringSeriesShadowRowForDurationSum(action: Action): boolean {
  return Boolean(action.isRecurringSeriesCompletedRow);
}

function recurrenceOccurrenceMultiplier(action: Action, rangeStart: Date, rangeEnd: Date): number {
  if (!validateRecurrence(action.event?.recurrence)) {
    return 1;
  }
  const summary = getRecurringInstancesInRangeCompletionSummary(action, rangeStart, rangeEnd);
  if (summary.inRangeCount === 0) {
    return 0;
  }
  const incompleteInRange = summary.inRangeCount - summary.completedInRangeCount;
  return incompleteInRange > 0 ? incompleteInRange : 0;
}

/** Sums planned duration; recurring actions count once per occurrence in [rangeStart, rangeEnd]. */
export function calculateTotalDurationWithRecurringInRange(
  actions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
): number {
  return actions.reduce((acc, action) => {
    if (skipRecurringSeriesShadowRowForDurationSum(action)) {
      return acc;
    }
    const minutes = calculateTotalMinutesBasedOnDuration(action.duration || '00:00:00');
    return acc + minutes * recurrenceOccurrenceMultiplier(action, rangeStart, rangeEnd);
  }, 0);
}

export function getTotalDurationOfActions(actions: Action[]) {
  const totalDuration = calculateTotalDuration(actions);

  const durationToHours = String(Math.floor(totalDuration / 60)).padStart(2, '0');
  const durationToMinutes = String(totalDuration % 60).padStart(2, '0');

  return `${durationToHours}:${durationToMinutes}`;
}

export function getTotalDurationOfIncompleteActions(actions: Action[]) {
  const incompleteActions = actions.filter((action) => action.progressStatus !== COMPLETE);
  return getTotalDurationOfActions(incompleteActions);
}

// @TODO: created this while waiting for PO green light about the final duration type
export function getTotalDurationOfActionsExtendedFormat(action: Action[]) {
  const minutes = action?.reduce((acc, curr) => acc + getSecondsFromDuration(curr.duration || '00:00:00'), 0);

  if (minutes === 0) {
    return '0h';
  }

  return humanDuration(minutes);
}

export function getTotalDurationOfPreferredActions(actions: Action[]) {
  return getTotalDurationOfActions(actions.filter((action) => action.dateOfStarring !== null));
}

export function getTotalDurationOfPreferredIncompleteActions(actions: Action[]) {
  const incompleteActions = actions.filter(
    (action) => action.progressStatus !== COMPLETE && action.dateOfStarring !== null,
  );
  return getTotalDurationOfActions(incompleteActions);
}

export function getTotalDurationOfActionsInRange(actions: Action[], rangeStart: Date, rangeEnd: Date) {
  const totalDuration = calculateTotalDurationWithRecurringInRange(actions, rangeStart, rangeEnd);

  const durationToHours = String(Math.floor(totalDuration / 60)).padStart(2, '0');
  const durationToMinutes = String(totalDuration % 60).padStart(2, '0');

  return `${durationToHours}:${durationToMinutes}`;
}

export function getTotalDurationOfPreferredActionsInRange(
  actions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
) {
  return getTotalDurationOfActionsInRange(
    actions.filter((action) => action.dateOfStarring !== null),
    rangeStart,
    rangeEnd,
  );
}

export function getTotalDurationOfIncompleteActionsInRange(
  actions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
) {
  const incompleteActions = actions.filter((action) => action.progressStatus !== COMPLETE);
  return getTotalDurationOfActionsInRange(incompleteActions, rangeStart, rangeEnd);
}

export function getTotalDurationOfPreferredIncompleteActionsInRange(
  actions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
) {
  const incompleteActions = actions.filter(
    (action) => action.progressStatus !== COMPLETE && action.dateOfStarring !== null,
  );
  return getTotalDurationOfActionsInRange(incompleteActions, rangeStart, rangeEnd);
}

export function getTotalDurationOfStarredActionsInRange(
  actions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
): string {
  const seconds = actions.reduce((acc, action) => {
    if (skipRecurringSeriesShadowRowForDurationSum(action)) {
      return acc;
    }
    if (!action?.dateOfStarring) {
      return acc;
    }
    const base = getSecondsFromDuration(action.duration || '00:00:00');
    return acc + base * recurrenceOccurrenceMultiplier(action, rangeStart, rangeEnd);
  }, 0);

  if (seconds === 0) {
    return '0h';
  }

  return humanDuration(seconds);
}

export function getDurationInMinutes(action: Action): number {
  const [hour, minute] = (action.duration || '00:00:00').split(':');
  return parseInt(hour, 10) * 60 + parseInt(minute, 10);
}

export function getCategoryData(categories?: Category[], key?: string) {
  const categoryId = filter(categories, (datum) => {
    if (datum.name === key) {
      return datum;
    }
  });
  return categoryId[0];
}

export function createActionUpdateWhere(actions: Action[], categoryId: string): ActionUpdateWhere[] {
  const actionUpdateWhere = actions?.map((action) => ({
    _set: {
      categoryId: categoryId,
      blockOrder: action.blockOrder ?? null,
      blockId: action.blockId ?? null,
    },
    where: {
      id: {
        _eq: action.id,
      },
    },
  }));

  return actionUpdateWhere;
}

function invalidateActionsQueriesForPlanIds(planIds: Iterable<string>) {
  for (const planId of planIds) {
    if (!planId) {
      continue;
    }
    const weekFilter = { queryKey: keys.actions.all._ctx.week(planId).queryKey };
    const weekAndPastFilter = { queryKey: keys.actions.all._ctx.weekAndPast(planId).queryKey };
    void queryClient.invalidateQueries(weekFilter);
    void queryClient.invalidateQueries(weekAndPastFilter);
    void queryClient.refetchQueries(weekFilter);
    void queryClient.refetchQueries(weekAndPastFilter);
  }
  const monthFilter = { queryKey: getMonthlyCalendarQueryKey() };
  void queryClient.invalidateQueries(monthFilter);
  void queryClient.refetchQueries(monthFilter);
  // Mark every cached actions query stale (refetches active ones; inactive ones refetch on
  // next mount). Deliberately no root refetchQueries: that would re-download every week the
  // user has ever opened this session on each mutation.
  void queryClient.invalidateQueries({ queryKey: keys.actions.all.queryKey });
}

export const refetchActions = (action: Action, weeklyPlanId?: string | null) => {
  const weeklyPlanToUse = weeklyPlanId && action.weeklyPlanId !== weeklyPlanId ? weeklyPlanId : action.weeklyPlanId;

  const invalidateWeeklyPlans = () => {
    const planIds = new Set<string>();
    if (weeklyPlanId && action.weeklyPlanId !== weeklyPlanId && action.weeklyPlanId) {
      planIds.add(action.weeklyPlanId);
    }
    if (weeklyPlanToUse) {
      planIds.add(String(weeklyPlanToUse));
    }
    invalidateActionsQueriesForPlanIds(planIds);
  };

  const invalidateCategory = () => {
    const categoryIdFromPath = location.pathname.match(/category-manager\/([\w-]+)/)?.[1];
    const categoryId = categoryIdFromPath;

    if (action.categoryId) {
      if (categoryId) {
        invalidateQueries([keys.actions.all._ctx.categoryManager(categoryId).queryKey]);
      }
      if (categoryId !== action.categoryId) {
        invalidateQueries([keys.actions.all._ctx.categoryManager(action.categoryId).queryKey]);
      }
    }
  };

  const invalidateProjects = () => {
    const projectIdFromPath = location.pathname.match(/\/projects\/([\w-]+)/)?.[1];
    const projectId = action.projectId || projectIdFromPath;

    if (action.projectId) {
      if (projectId) {
        invalidateQueries([keys.project.detail(projectId).queryKey]);
      }
      if (projectId !== action.projectId) {
        invalidateQueries([keys.project.detail(action.projectId).queryKey]);
      }
    }
  };

  const invalidatePromises = () => {
    if (action.promises) {
      refetchQueries([keys.actions.leveragedAndCommitted.queryKey]);
    }
  };

  invalidateWeeklyPlans();
  invalidateCategory();
  invalidateProjects();
  invalidatePromises();

  queryClient.invalidateQueries({ queryKey: actionsKeys.actionsAndBlock });
  queryClient.invalidateQueries({ queryKey: [...celebrateReflectionWeekScopeRoot] });
};

export function removeCurrentFromActionsList(actions: Action[], currentId: string) {
  return actions.filter((item) => item.id !== currentId);
}

export function getIncompleteActions(actions: Action[], iterates = 'categoryOrder', orders: 'asc' | 'desc' = 'asc') {
  return orderBy(
    actions.filter((item) => item.progressStatus !== COMPLETE),
    iterates,
    orders,
  );
}

export function getCompletedActions(actions: Action[], iterates = 'categoryOrder', orders: 'asc' | 'desc' = 'asc') {
  return orderBy(
    actions.filter((item) => item.progressStatus === COMPLETE),
    iterates,
    orders,
  );
}

export function getCanceledActions(actions: Action[], iterates = 'categoryOrder', orders: 'asc' | 'desc' = 'asc') {
  return orderBy(
    actions.filter((item) => item.isCanceled),
    iterates,
    orders,
  );
}

export function getActionsByCategoryId(actions: Action[], categoryId: string | null) {
  return actions.filter((action) => action.categoryId === categoryId);
}

export function getActionsNotMatchCategoryId(actions: Action[], categoryId: string | null) {
  return actions.filter((action) => action.categoryId !== categoryId);
}

export function updateActionsOrder(actions: Action[], orderField: OrderFieldType): Action[] {
  return actions.map((action, index) => {
    return {
      ...action,
      [orderField]: index + 1,
    };
  });
}
function findNearestIncompleteItem(actions: Action[], updatedActionOrder: number, orderField: OrderFieldType) {
  return actions.reduce((nearestItem, currentItem) => {
    const currentItemOrder = currentItem[orderField] ?? 0;
    const nearestItemOrder = nearestItem[orderField] ?? 0;

    if (currentItemOrder < updatedActionOrder && currentItemOrder > nearestItemOrder) {
      return currentItem;
    }
    return nearestItem;
  }, actions[0]);
}

function updateOrderByNearestIncompleteItem(
  actions: Action[],
  nearestIncompleteItemOrder: number,
  orderField: OrderFieldType,
) {
  return actions.map((item) => {
    const order = item[orderField] ?? 0;

    if (order > nearestIncompleteItemOrder) {
      return { ...item, [orderField]: order + 1 };
    }
    return item;
  });
}

function findIndexById(actions: Action[], actionId: string) {
  return actions.findIndex((item) => item.id === actionId);
}

function fixCollisionOrder(actions: Action[], orderField: OrderFieldType) {
  return actions.map((item, index) => ({
    ...item,
    [orderField]: index + 1,
  }));
}

function sortByOrder(actions: Action[], orderField: OrderFieldType) {
  return actions.sort((a, b) => {
    const aOrder = a[orderField] ?? 0;
    const bOrder = b[orderField] ?? 0;

    return aOrder - bOrder;
  });
}

export function updateOrderOnIncompleteAction(actions: Action[], updatedAction: Action, orderField: OrderFieldType) {
  const incompleteItems = getIncompleteActions(actions);

  if (incompleteItems.length === 0) {
    let modifiedItems = updateOrderByNearestIncompleteItem(
      removeCurrentFromActionsList(actions, updatedAction.id),
      0,
      orderField,
    );
    modifiedItems = sortByOrder([...modifiedItems, { ...updatedAction, [orderField]: 1 }], orderField);

    return fixCollisionOrder(modifiedItems, orderField);
  }

  const nearestIncompleteItem = findNearestIncompleteItem(incompleteItems, updatedAction[orderField] ?? 0, orderField);

  const nearestIncompleteItemOrder = nearestIncompleteItem[orderField] ?? 0;

  const modifiedItems = updateOrderByNearestIncompleteItem(actions, nearestIncompleteItemOrder, orderField);

  const updatedItemIndex = findIndexById(actions, updatedAction.id);

  modifiedItems[updatedItemIndex] = {
    ...updatedAction,
    [orderField]: nearestIncompleteItemOrder + 1,
  };

  return fixCollisionOrder(sortByOrder(modifiedItems, orderField), orderField);
}

export function hasProgressStatusUpdated(actions: Action[], updatedAction: Action) {
  const cachedAction = actions.find((action) => action.id === updatedAction.id);
  return updatedAction.progressStatus !== cachedAction?.progressStatus;
}

export function onCompleteAction(actions: Action[], updatedAction: Action, orderField: OrderFieldType) {
  if (!hasProgressStatusUpdated(actions, updatedAction)) {
    return actions;
  }

  let filteredActions = orderBy(getActionsByCategoryId(actions, updatedAction.categoryId), orderField, 'asc');
  filteredActions = updateActionsOrder(removeCurrentFromActionsList(filteredActions, updatedAction.id), orderField);

  const sortedActions = [...filteredActions, updatedAction];

  const rest = getActionsNotMatchCategoryId(actions, updatedAction.categoryId);

  return [...rest, ...sortedActions];
}

export function onIncompleteAction(actions: Action[], updatedAction: Action, orderField: OrderFieldType) {
  if (!hasProgressStatusUpdated(actions, updatedAction)) {
    return actions;
  }

  let filteredActions = getActionsByCategoryId(actions, updatedAction.categoryId);

  const sortedActions = updateOrderOnIncompleteAction(filteredActions, updatedAction, orderField);

  const rest = getActionsNotMatchCategoryId(actions, updatedAction.categoryId);

  return [...rest, ...sortedActions];
}

export function getActionById(actions: Action[], actionId: string) {
  return actions.find((action) => action.id === actionId);
}

export function getNonBlockActionsByCategoryId(actions: Action[], categoryId: string) {
  return getActionsByCategoryId(
    actions.filter((action) => action.blockId === null),
    categoryId,
  );
}

export function getDailyActions(actions: Action[], selectedDate: Date) {
  return actions.filter((action) => {
    if (!action.scheduledDate) {
      return false;
    }

    const eventDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);

    return formatDateToString(eventDate) === formatDateToString(selectedDate);
  });
}

export function getWeeklyActions(actions: Action[], weeklyPlanId: string) {
  return actions.filter((action) => action.weeklyPlanId === weeklyPlanId);
}

export function getIncompleteActionsByBlockId(blockId: string, actions: Action[]) {
  return actions.filter((action) => action.blockId === blockId && action.progressStatus !== COMPLETE);
}

export function getActionsByBlockId(blockId: string, actions: Action[], includeCanceledActions = false): Action[] {
  const blockActions = actions.filter(
    (action) => action.blockId === blockId && (includeCanceledActions || !action.isCanceled),
  );
  return blockActions.map((action) => ({ ...action, isLastBlockAction: blockActions.length === 1 }));
}

export function createActionDuplicate(
  source: Action,
  scheduledDate: string,
  scheduledTime: string | null,
  weeklyPlanId: string,
) {
  const action: QuickCreateActionPayload = {
    id: uuidv4(),
    categoryId: source.categoryId as string,
    dateOfStarring: source.dateOfStarring,
    duration: source.duration as string,
    isLocked: source.isLocked,
    notes: source.notes,
    progressStatus: source.progressStatus,
    projectId: source.projectId ?? null,
    scheduledDate,
    scheduledTime,
    timezone: source.timezone,
    title: source.title,
    gmtOffset: source.gmtOffset,
    weeklyPlanId,
  };
  if (source.blockId) {
    action.blockId = source.blockId;
  }
  return pairedActionScheduleFields(action as unknown as Record<string, unknown>) as QuickCreateActionPayload;
}

const isInCurrentView = (eventStart: Date, currentDate: Date, view: 'day' | 'week' | 'month' | 'year') => {
  if (view === 'day') return isSameDay(eventStart, currentDate);
  if (view === 'week') return isSameWeek(eventStart, currentDate, { weekStartsOn: 1 });
  if (view === 'month') return isSameMonth(eventStart, currentDate);
  if (view === 'year') return isSameYear(eventStart, currentDate);
  return false;
};

interface GetOccurrencesOptions {
  start: Date;
  frequency: string;
  interval: number;
  maxOccurrences?: number;
  until?: Date;
  byDay?: { day: string }[];
  exceptions?: Date[];
  count?: number;
  /** Callers that only consume occurrences on/after this date pass it so long series don't enumerate their full history. */
  rangeStart?: Date;
}

function normalizeByDay(byDay: { day: string }[] | string[] | undefined): { day: string }[] {
  if (!Array.isArray(byDay) || byDay.length === 0) return [];
  return byDay.map((d) => (typeof d === 'string' ? { day: d.toLowerCase() } : { day: (d?.day ?? '').toLowerCase() })).filter((d) => d.day);
}

export const getOccurrences = ({
  start,
  frequency,
  interval: intervalParam,
  maxOccurrences,
  until,
  byDay = [],
  exceptions = [],
  count,
  rangeStart,
}: GetOccurrencesOptions): Date[] => {
  const occurrences: Date[] = [];
  const interval = Math.max(1, Number(intervalParam) || 1);

  if (!start || !(start instanceof Date) || Number.isNaN(start.getTime())) {
    return occurrences;
  }

  let currentDate = new Date(start.getTime());
  let occurrenceCount = 0;

  const normalizedFreq = frequency?.toLowerCase?.();
  const isWeekly = normalizedFreq === 'weekly' || normalizedFreq === 'week';
  const normalizedByDay = normalizeByDay(byDay);

  const incrementMap: Record<string, { days?: number; weeks?: number; months?: number; years?: number }> = {
    daily: { days: interval },
    day: { days: interval },
    weekly: normalizedByDay?.length ? { days: 1 } : { weeks: interval },
    week: normalizedByDay?.length ? { days: 1 } : { weeks: interval },
    monthly: { months: interval },
    month: { months: interval },
    yearly: { years: interval },
    year: { years: interval },
  };

  const incrementInterval = incrementMap[normalizedFreq] ?? incrementMap.daily;

  const hasValidUntil = until && !Number.isNaN(until.getTime());

  // Occurrence-capped rules must enumerate from the series start; unbounded ones can skip ahead.
  // This loop's exception branch always advances +1 day, which phase-shifts every shape whose
  // normal advance is NOT +1 day. Daily interval 1 and weekly by_day interval 1 advance +1 day
  // in both branches, so they keep the clamp even with pre-window exceptions.
  if (rangeStart && !maxOccurrences && (count === undefined || count === null)) {
    const exceptionsCanShiftPhase = !(
      ((normalizedFreq === 'daily' || normalizedFreq === 'day') && interval === 1) ||
      (isWeekly && normalizedByDay.length > 0 && interval === 1)
    );
    currentDate = fastForwardRecurrenceCursor(
      currentDate,
      rangeStart,
      normalizedFreq,
      interval,
      normalizedByDay,
      exceptionsCanShiftPhase ? exceptions : undefined,
    );
  }

  const maxIterations = 10000;
  let iterations = 0;

  while (
    (!hasValidUntil || isBefore(currentDate, until) || isEqual(currentDate, until)) &&
    (!maxOccurrences || occurrenceCount < maxOccurrences) &&
    (count === undefined || count === null || occurrenceCount < count) &&
    iterations < maxIterations
  ) {
    iterations++;
    if (Number.isNaN(currentDate.getTime())) break;

    if (isExceptionDate(currentDate, exceptions)) {
      currentDate = add(currentDate, { days: 1 });
      continue;
    }

    if (isWeekly && normalizedByDay.length && !isValidWeeklyDay(currentDate, normalizedByDay)) {
      currentDate = add(currentDate, { days: 1 });
      continue;
    }

    occurrences.push(new Date(currentDate));
    occurrenceCount++;

    if (isWeekly && normalizedByDay.length && interval > 1 && shouldIncrementByWeek(currentDate, normalizedByDay)) {
      const daysFromMonday = (currentDate.getDay() + 6) % 7;
      currentDate = add(currentDate, { days: -daysFromMonday + interval * 7 });
      continue;
    }

    currentDate = add(currentDate, incrementInterval);
  }

  return occurrences;
};

export const filterIncompleteActions = (
  actions: Action[],
  currentDate: Date,
  view: 'day' | 'week' | 'month' | 'year',
  endDate?: Date,
): Action[] => {
  if (!currentDate || !(currentDate instanceof Date) || Number.isNaN(currentDate.getTime())) {
    return actions;
  }

  return actions.filter((action) => {
    const recurrence = action.event?.recurrence?.rules?.[0];

    if (!recurrence) return true;
    if (!action.scheduledDate) return true;

    const start = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);
    if (!start || Number.isNaN(start.getTime())) return true;
    const interval = Math.max(1, Number(recurrence.interval) || 1);

    // Handle until date properly - don't convert fallback endDate since it's already in local time
    let until: Date;
    if (recurrence.until) {
      const [fullEndDate] = recurrence.until.split('T');
      until = utcToLocalDate(fullEndDate, action.scheduledTime, action.timezone, action.gmtOffset);
    } else {
      const fallbackEnd = endDate instanceof Date && !Number.isNaN(endDate.getTime()) ? endDate : currentDate;
      until = new Date(fallbackEnd);
      until.setHours(23, 59, 59, 999); // Set to end of day
    }

    if (Number.isNaN(until.getTime())) return true;

    const count = recurrence?.count;
    const byDay = recurrence?.by_day;

    const exceptions =
      action.event?.recurrence != null ? getExceptionDates(action.event.recurrence, action) : [];

    // Occurrences before the visible period are never checked below; let getOccurrences skip them.
    const rangeStart =
      view === 'day'
        ? startOfDay(currentDate)
        : view === 'week'
          ? startOfWeek(currentDate, { weekStartsOn: 1 })
          : view === 'month'
            ? startOfMonth(currentDate)
            : startOfYear(currentDate);

    const occurrences = getOccurrences({
      start,
      frequency: recurrence.frequency,
      interval,
      count,
      until,
      byDay,
      exceptions,
      rangeStart,
    });

    // For day view, use direct isSameDay comparison since occurrences are already in local time
    return occurrences.some((date) => {
      if (view === 'day') {
        return isSameDay(date, currentDate);
      }
      return isInCurrentView(date, currentDate, view);
    });
  });
};
